import { type FC, useState, useEffect, useRef } from 'react'

import { cn } from '~/shadcn/lib/utils'
import { Button } from '~/shadcn/ui/button/button'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '~/shadcn/ui/popover'
import { Calendar } from '~/shadcn/ui/calendar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/shadcn/ui/select'
import { DateInput } from '~/@mid_ui-form/date-input/DateInput'
import { buttonVariants } from '~/shadcn/ui/button/buttonVariants'

export type DateRangePickerProps = {
  /** Click handler for applying the updates from DateRangePicker. */
  onUpdate?: (values: { range: DateRange; rangeCompare?: DateRange }) => void
  /** Initial value for start date */
  initialDateFrom?: Date | string
  /** Initial value for end date */
  initialDateTo?: Date | string
  /** Initial value for start date for compare */
  initialCompareFrom?: Date | string
  /** Initial value for end date for compare */
  initialCompareTo?: Date | string
  /** Alignment of popover */
  align?: 'start' | 'center' | 'end'
  /** Option for locale */
  locale?: string
  /** Option for showing compare feature */
  showCompare?: boolean
}

const formatDate = (date: Date, locale: string = 'sk-SK'): string => {
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const getDateAdjustedForTimezone = (dateInput: Date | string): Date => {
  if (typeof dateInput === 'string') {
    // Split the date string to get year, month, and day parts
    const parts = dateInput.split('-').map((part) => parseInt(part, 10))
    // Create a new Date object using the local timezone
    // Note: Month is 0-indexed, so subtract 1 from the month part
    const date = new Date(parts[0], parts[1] - 1, parts[2])
    return date
  } else {
    // If dateInput is already a Date object, return it directly
    return dateInput
  }
}

type DateRange = {
  from: Date
  to: Date | undefined
}

type Preset = {
  name: string
  label: string
}

// Define presets
const PRESETS: Preset[] = [
  { name: 'thisMonth', label: 'Tento mesiac' },
  { name: 'thisYear', label: 'Tento rok' },
  { name: 'lastYear', label: 'Minulý rok' },
  { name: 'allTime', label: 'Neobmedzene' },
]

/** The DateRangePicker component allows a user to select a range of dates */
export const DateRangePicker: FC<DateRangePickerProps> & {
  filePath: string
} = ({
  initialDateFrom = new Date(new Date().setHours(0, 0, 0, 0)),
  initialDateTo,
  initialCompareFrom,
  initialCompareTo,
  onUpdate,
  align = 'end',
  locale = 'sk-SK',
}): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false)

  const [range, setRange] = useState<DateRange>({
    from: getDateAdjustedForTimezone(initialDateFrom),
    to: initialDateTo
      ? getDateAdjustedForTimezone(initialDateTo)
      : getDateAdjustedForTimezone(initialDateFrom),
  })
  const [rangeCompare, setRangeCompare] = useState<DateRange | undefined>(
    initialCompareFrom
      ? {
          from: new Date(new Date(initialCompareFrom).setHours(0, 0, 0, 0)),
          to: initialCompareTo
            ? new Date(new Date(initialCompareTo).setHours(0, 0, 0, 0))
            : new Date(new Date(initialCompareFrom).setHours(0, 0, 0, 0)),
        }
      : undefined
  )

  // Refs to store the values of range and rangeCompare when the date picker is opened
  const openedRangeRef = useRef<DateRange | undefined>(undefined)
  const openedRangeCompareRef = useRef<DateRange | undefined>(undefined)

  const [selectedPreset, setSelectedPreset] = useState<string | undefined>(
    undefined
  )

  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  )

  useEffect(() => {
    const handleResize = (): void => {
      setIsSmallScreen(window.innerWidth < 768)
    }

    window.addEventListener('resize', handleResize)

    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const getPresetRange = (presetName: string): DateRange => {
    const preset = PRESETS.find(({ name }) => name === presetName)
    if (!preset) throw new Error(`Unknown date range preset: ${presetName}`)
    const from = new Date()
    const to = new Date()

    if (preset.name === 'allTime') {
      from.setDate(1)
      from.setMonth(0)
      from.setHours(0, 0, 0, 0)
      from.setFullYear(2000)
      return { from, to }
    }

    if (preset.name === 'thisMonth') {
      from.setDate(1)
      from.setHours(0, 0, 0, 0)
      to.setHours(23, 59, 59, 999)
      return { from, to }
    }

    if (preset.name === 'thisYear') {
      from.setMonth(0)
      from.setDate(1)
      from.setHours(0, 0, 0, 0)
      to.setMonth(11)
      to.setDate(31)
      to.setHours(23, 59, 59, 999)
      return { from, to }
    }

    if (preset.name === 'lastYear') {
      from.setMonth(from.getMonth() - 12)
      from.setDate(1)
      from.setHours(0, 0, 0, 0)
      to.setMonth(-1)
      to.setDate(31)
      to.setHours(23, 59, 59, 999)
      return { from, to }
    }

    return { from, to }
  }

  const setPreset = (preset: string): void => {
    const range = getPresetRange(preset)
    setRange(range)
    if (rangeCompare) {
      const rangeCompare = {
        from: new Date(
          range.from.getFullYear() - 1,
          range.from.getMonth(),
          range.from.getDate()
        ),
        to: range.to
          ? new Date(
              range.to.getFullYear() - 1,
              range.to.getMonth(),
              range.to.getDate()
            )
          : undefined,
      }
      setRangeCompare(rangeCompare)
    }
  }

  const checkPreset = (): void => {
    for (const preset of PRESETS) {
      const presetRange = getPresetRange(preset.name)

      const normalizedRangeFrom = new Date(range.from)
      normalizedRangeFrom.setHours(0, 0, 0, 0)
      const normalizedPresetFrom = new Date(
        presetRange.from.setHours(0, 0, 0, 0)
      )

      const normalizedRangeTo = new Date(range.to ?? 0)
      normalizedRangeTo.setHours(0, 0, 0, 0)
      const normalizedPresetTo = new Date(
        presetRange.to?.setHours(0, 0, 0, 0) ?? 0
      )

      if (
        normalizedRangeFrom.getTime() === normalizedPresetFrom.getTime() &&
        normalizedRangeTo.getTime() === normalizedPresetTo.getTime()
      ) {
        setSelectedPreset(preset.name)
        return
      }
    }

    setSelectedPreset(undefined)
  }

  const resetValues = (): void => {
    setRange({
      from:
        typeof initialDateFrom === 'string'
          ? getDateAdjustedForTimezone(initialDateFrom)
          : initialDateFrom,
      to: initialDateTo
        ? typeof initialDateTo === 'string'
          ? getDateAdjustedForTimezone(initialDateTo)
          : initialDateTo
        : typeof initialDateFrom === 'string'
          ? getDateAdjustedForTimezone(initialDateFrom)
          : initialDateFrom,
    })
    setRangeCompare(
      initialCompareFrom
        ? {
            from:
              typeof initialCompareFrom === 'string'
                ? getDateAdjustedForTimezone(initialCompareFrom)
                : initialCompareFrom,
            to: initialCompareTo
              ? typeof initialCompareTo === 'string'
                ? getDateAdjustedForTimezone(initialCompareTo)
                : initialCompareTo
              : typeof initialCompareFrom === 'string'
                ? getDateAdjustedForTimezone(initialCompareFrom)
                : initialCompareFrom,
          }
        : undefined
    )
  }

  useEffect(() => {
    checkPreset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range])

  const PresetButton = ({
    preset,
    label,
    isSelected,
  }: {
    preset: string
    label: string
    isSelected: boolean
  }): React.ReactElement => (
    <Button
      className={cn(
        'w-35 h-11 text-sm text-muted rounded-md justify-start',
        isSelected && 'pointer-events-none text-accent bg-accent-foreground'
      )}
      variant="ghost"
      onClick={() => {
        setPreset(preset)
      }}>
      {label}
    </Button>
  )

  // Helper function to check if two date ranges are equal
  const areRangesEqual = (a?: DateRange, b?: DateRange): boolean => {
    if (!a || !b) return a === b // If either is undefined, return true if both are undefined
    return (
      a.from.getTime() === b.from.getTime() &&
      (!a.to || !b.to || a.to.getTime() === b.to.getTime())
    )
  }

  useEffect(() => {
    if (isOpen) {
      openedRangeRef.current = range
      openedRangeCompareRef.current = rangeCompare
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <Popover
      modal={true}
      open={isOpen}
      onOpenChange={(open: boolean) => {
        if (!open) {
          resetValues()
        }
        setIsOpen(open)
      }}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            buttonVariants({ variant: 'datePicker', size: 'datePicker' }),
            'w-full md:w-fit flex flex-row items-center justify-start lg:justify-center'
          )}>
          {`${formatDate(range.from, locale)}${
            range.to != null ? ' - ' + formatDate(range.to, locale) : ''
          }`}
        </div>
      </PopoverTrigger>
      {isSmallScreen && (
        <PopoverAnchor style={{ position: 'fixed', top: 0, left: 0 }} />
      )}
      <PopoverContent
        align={align}
        className="flex flex-col w-screen md:w-auto h-[98svh] md:h-auto justify-between">
        <div className="flex">
          {!isSmallScreen && (
            <div className="flex w-full flex-col items-start gap-1 pr-2 pb-6 border-r border-border/20">
              {PRESETS.map((preset) => (
                <PresetButton
                  key={preset.name}
                  preset={preset.name}
                  label={preset.label}
                  isSelected={selectedPreset === preset.name}
                />
              ))}
            </div>
          )}
          <div className="flex flex-col w-full">
            <div className="flex flex-col gap-2">
              {rangeCompare != null && (
                <div className="flex gap-2">
                  <DateInput
                    value={rangeCompare?.from}
                    onChange={(date) => {
                      if (rangeCompare) {
                        const compareToDate =
                          rangeCompare.to == null || date > rangeCompare.to
                            ? date
                            : rangeCompare.to
                        setRangeCompare((prevRangeCompare) => ({
                          ...prevRangeCompare,
                          from: date,
                          to: compareToDate,
                        }))
                      } else {
                        setRangeCompare({
                          from: date,
                          to: new Date(),
                        })
                      }
                    }}
                  />
                  <div className="py-1">-</div>
                  <DateInput
                    value={rangeCompare?.to}
                    onChange={(date) => {
                      if (rangeCompare && rangeCompare.from) {
                        const compareFromDate =
                          date < rangeCompare.from ? date : rangeCompare.from
                        setRangeCompare({
                          ...rangeCompare,
                          from: compareFromDate,
                          to: date,
                        })
                      }
                    }}
                  />
                </div>
              )}
            </div>
            {isSmallScreen && (
              <Select
                defaultValue={selectedPreset}
                onValueChange={(value) => {
                  setPreset(value)
                }}>
                <SelectTrigger className="w-full mx-auto md:mb-2">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent className="bg-white border-border">
                  {PRESETS.map((preset) => (
                    <SelectItem key={preset.name} value={preset.name}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div>
              <Calendar
                mode="range"
                onSelect={(value: { from?: Date; to?: Date } | undefined) => {
                  if (value?.from != null) {
                    setRange({ from: value.from, to: value?.to })
                  }
                }}
                selected={range}
                numberOfMonths={2}
                defaultMonth={
                  new Date(new Date().setMonth(new Date().getMonth() - 1))
                }
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row grow md:grow-0 justify-end gap-2 md:py-2 md:pr-4 border-t border-border/20">
          <div className="hidden md:flex items-center pr-4">
            <span className="text-sm font-semibold text-primary">{`${formatDate(range.from, locale)}${
              range.to != null ? ' - ' + formatDate(range.to, locale) : ''
            }`}</span>
          </div>
          <div className="flex flex-row gap-2">
            <Button
              onClick={() => {
                setIsOpen(false)
                resetValues()
              }}
              variant="outline"
              className="flex grow md:grow-0 text-border-strong border-border-strong">
              Zrušiť
            </Button>
            <Button
              className="flex grow md:grow-0 text-primary-foreground"
              onClick={() => {
                setIsOpen(false)
                if (
                  !areRangesEqual(range, openedRangeRef.current) ||
                  !areRangesEqual(rangeCompare, openedRangeCompareRef.current)
                ) {
                  onUpdate?.({ range, rangeCompare })
                }
              }}>
              Použiť
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

DateRangePicker.displayName = 'DateRangePicker'
DateRangePicker.filePath =
  'libs/shared/ui-kit/src/lib/date-range-picker/date-range-picker.tsx'
